import { useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useForm } from 'react-hook-form';
import { WorkflowComponentProps } from '../../../../Workflow/Workflow.meta';
import { dataToForm, FormValues } from './ExpensesTotal.meta';
import { useSaveExpensesMutation } from '../../api/useSaveExpensesMutation';
import { useEstimateExpensesMutation } from '../../api/useEstimateExpensesMutation';
import { showOPLConfirmationDialog } from '../../../../helpers/showOPLConfirmationDialog';
import { toast } from '../../../../CommonComponents/OPL/Toast/Toast';
import { MESSAGES } from '../../../../CommonConstants';
import { ExpenseFrequency, ExpensesDTO, ExpensesMode } from '../../api/useExpensesQuery';

interface Params {
  clientId: string;
  data: ExpensesDTO;
  workflowProps?: WorkflowComponentProps;
}

export const useExpensesTotal = ({ clientId, data, workflowProps }: Params) => {
  const formBag = useForm<FormValues>({
    defaultValues: dataToForm(data),
  });

  const saveExpensesMutation = useSaveExpensesMutation({ clientId });

  const estimateExpensesMutation = useEstimateExpensesMutation();

  useEffect(() => {
    if (isEmpty(formBag.formState.dirtyFields)) {
      ADV.util.Router.unregisterNavigateAwayCallback();
    } else {
      ADV.util.Router.registerNavigateAwayCallback(showOPLConfirmationDialog);
    }
  }, [formBag.formState]);

  const onSubmit = formBag.handleSubmit(async values => {
    try {
      await saveExpensesMutation.mutateAsync({
        clientId,
        expensesInputMode: ExpensesMode.Total,
        totalExpenses: {
          Name: 'Total',
          Amount: values.amount === null ? 0 : values.amount,
          Frequency: values.frequency,
        },
      });

      formBag.reset({ ...values, amount: values.amount === null ? 0 : values.amount });

      if (workflowProps) {
        workflowProps.onNext();
      } else {
        toast.success('Your expenses were successfully saved.');
      }
    } catch (error) {
      console.error(error);
      toast.error(MESSAGES.GENERIC_ERROR);
    }
  });

  const estimateExpenses = async () => {
    try {
      const { TotalEstimatedValue } = await estimateExpensesMutation.mutateAsync({ clientId });

      formBag.setValue('amount', TotalEstimatedValue);
      formBag.setValue('frequency', ExpenseFrequency.Monthly);
    } catch (error) {
      console.error(error);
      toast.error(MESSAGES.GENERIC_ERROR);
    }
  };

  const frequency = formBag.watch('frequency');

  return {
    formBag,
    frequency,
    onSubmit,
    estimateExpenses,
    estimateExpensesMutation,
  };
};
